import { Popover } from '@headlessui/react';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';

import { truncateLabel } from '@/utils';

import {
  SUMMARY_XP_FORMAT as FMT,
  EXPORT_REPORT_SCALE as ERS,
  EXPORT_REPORT_LAYOUT as ERL,
} from '../types';

import {
  isUsingInsulation,
  SUMMARY_TOOL_MENU_XLS_SINGLE_COL as DSC, // drawing single column
  SUMMARY_TOOL_MENU_XLS_MULTIP_COL as DMC, // drawing multiple column
  SUMMARY_TOOL_MENU_PRJ_SINGLE_COL as PSC, // project single column
  SUMMARY_TOOL_MENU_PRJ_MULTIP_COL as PMC, // project multiple column
  SUMMARY_TOOL_MENU_ITEMIZED_EXPORT as ITX,
  SUMMARY_SEV_BY_DRAWING as SEVD,
  SUMMARY_SEV_BY_PROJECT as SEVP,
  SUMMARY_TOOL_MENU_WRD_PROJECT_MC as SWPMC,
  SUMMARY_TOOL_MENU_WRD_PROJECT_SC as SWPSC,
  SUMMARY_INSULATION_BY_DRAWING as INSD,
  SUMMARY_INSULATION_BY_PROJECT as INSP,
  SUMMARY_POUNDAGE_BY_PAGE as PPAG,
  SUMMARY_POUNDAGE_BY_PROJECT as PPRJ,
  SUMMARY_PIPES_BY_PAGE as PIPAG,
  SUMMARY_PIPES_BY_PROJECT as PIPRJ,
} from '@/config';

export type ExportFuncType = (
  /** xls | doc */
  format: string,
  /** for project or drawing */
  scale: string,
  /** single-column | multi-column */
  layout?: string,
  group?: boolean,
  segment_size?: number,
) => void;

export type ExportToolProps = {
  downloading?: boolean;
  exportTo: ExportFuncType;
  exportSEV: (scale: string) => void;
  exportInsulation: (scale: string) => void;
  exportPoundage: () => void;
  exportPoundageProject: () => void;
  exportPipes: () => void;
  exportPipesProject: () => void;
};

/**
 * summary export
 *
 * @param param0
 * @returns
 */
export const ExportDrawingTool = ({
  exportTo,
  exportSEV,
  exportInsulation,
  exportPoundage,
  exportPoundageProject,
  exportPipes,
  exportPipesProject,
}: ExportToolProps) => (
  <Popover className="relative">
    <Popover.Button className="btn-title-tool mr-2">
      <CloudArrowUpIcon className="size-4 text-blue-600 inline-block" />
      <span className="text-sm px-1">Export</span>
    </Popover.Button>

    <Popover.Panel className="absolute z-500 ">
      {/* this class determine the with of menu */}
      <ul className="btn-popup-menu w-52">
        {/* multiple columns for project */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.E, ERS.P, ERL.M)}
        >
          {truncateLabel(PMC)}
        </Popover.Button>
        {/* multiple columns for page */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.E, ERS.D, ERL.M)}
        >
          {truncateLabel(DMC)}
        </Popover.Button>
        {/* single column for project */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.E, ERS.P, ERL.S)}
        >
          {truncateLabel(PSC)}
        </Popover.Button>
        {/* single column for page */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.E, ERS.D, ERL.S)}
        >
          {truncateLabel(DSC)}
        </Popover.Button>
        {/* itemized export - @2025/05/28 */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.E, ERS.D, ERL.M, false, 5)}
        >
          {truncateLabel(ITX)}
        </Popover.Button>
        {/* SEV export - @2025/11/14 */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportSEV(ERS.D)}
        >
          {truncateLabel(SEVD)}
        </Popover.Button>
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportSEV(ERS.P)}
        >
          {truncateLabel(SEVP)}
        </Popover.Button>
        {/* == added at 2026/04/14 */}
        {/* multiple columns for project - doc */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.D, ERS.P, ERL.M)}
        >
          {truncateLabel(SWPMC)}
        </Popover.Button>
        {/* single column for project - doc */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.D, ERS.P, ERL.S)}
        >
          {truncateLabel(SWPSC)}
        </Popover.Button>
        {/* == insulation export - @2026/06/15 */}
        {isUsingInsulation && (
          <Popover.Button
            className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
            onClick={() => exportInsulation(ERS.D)}
          >
            {truncateLabel(INSD)}
          </Popover.Button>
        )}
        {isUsingInsulation && (
          <Popover.Button
            className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
            onClick={() => exportInsulation(ERS.P)}
          >
            {truncateLabel(INSP)}
          </Popover.Button>
        )}
        {/* == poundage export - @2026/08/05 */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportPoundage()}
        >
          {truncateLabel(PPAG)}
        </Popover.Button>
        {/* == poundage by project export - @2026/08/17 */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportPoundageProject()}
        >
          {truncateLabel(PPRJ)}
        </Popover.Button>
        {/* == pipes export - @2026/10/07 */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportPipes()}
        >
          {truncateLabel(PIPAG)}
        </Popover.Button>
        {/* == pipes by project export - @2026/10/07 */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportPipesProject()}
        >
          {truncateLabel(PIPRJ)}
        </Popover.Button>
      </ul>
    </Popover.Panel>
  </Popover>
);
